import { FC } from "react";
import { X } from "lucide-react";
import type { ContactAttempt } from "@myapp/contract";
import { formatDayMonthDE, formatTimeDE, formatWeekdayDateDE } from "../../../../shared/helpers/date";

interface Props {
  attempt: ContactAttempt;
  /** 1-based position in the list. */
  position: number;
  onRemove?: (attempt: ContactAttempt) => void;
}

/**
 * One recorded attempt: the number, what was tried, and when — date *and* time, because a call at
 * 08:05 and one at 17:40 are different attempts. The author is dropped on a phone, where the row
 * has no room for it; the date shortens to „12.10." for the same reason.
 */
export const AttemptRow: FC<Props> = ({ attempt, position, onRemove }) => (
  <li className="grid min-h-[52px] grid-cols-[26px_minmax(0,1fr)_auto_32px] items-center gap-[10px] border-t border-slate-200 py-2.5 text-sm first:min-h-[42px] first:border-t-0 first:pt-0 sm:grid-cols-[28px_minmax(0,1fr)_auto_32px] sm:gap-3 sm:text-[15px]">
    <span
      aria-hidden="true"
      className="grid h-[26px] w-[26px] place-items-center rounded-full bg-[#D64541] text-[13px] font-bold text-white sm:h-7 sm:w-7"
    >
      {position}
    </span>
    <span className="break-words text-slate-800">
      {attempt.note || "Kontaktversuch"}
      <span className="hidden text-slate-600 sm:inline"> · {attempt.createdByName}</span>
    </span>
    <span className="whitespace-nowrap text-right leading-tight text-slate-600 [font-variant-numeric:tabular-nums]">
      <b className="block font-medium text-slate-700">
        <span className="sm:hidden">{formatDayMonthDE(attempt.attemptedAt)}</span>
        <span className="hidden sm:inline">{formatWeekdayDateDE(attempt.attemptedAt)}</span>
      </b>
      {formatTimeDE(attempt.attemptedAt)}
    </span>
    {onRemove ? (
      <button
        type="button"
        onClick={() => onRemove(attempt)}
        aria-label={`${position}. Versuch löschen`}
        title="Versuch löschen"
        className="grid h-8 w-8 justify-self-end place-items-center rounded-full text-slate-600 transition-colors duration-150 hover:bg-[#D6454114] hover:text-[#D64541]"
      >
        <X size={16} aria-hidden="true" />
      </button>
    ) : (
      <span />
    )}
  </li>
);
